import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2DataGrid,
  V2EmptyState,
  V2Button,
  V2FormGrid,
  V2FormField,
  Modal,
  LoadingState,
  StatePanel,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import type { PurchaseOrderListResponse } from '../../lib/types/procurement.js';
import { orderStatusOf } from '../../lib/types/procurement.js';

const OPEN_ORDER_STATUSES = new Set(['submitted', 'approved', 'partially_received']);

interface SupplierRow {
  readonly id: string;
  readonly supplierPartyRef: string;
  readonly displayName: string;
  readonly openOrders: number;
  readonly totalOrders: number;
  readonly lastOrderDate: string;
}

export function SuppliersPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [query, setQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<'organization' | 'individual'>('organization');
  const [mobile, setMobile] = useState('');
  const [phone, setPhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [economicCode, setEconomicCode] = useState('');
  const [address, setAddress] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // B42: Fetch suppliers with limit=200
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?role=supplier&limit=200` : null,
    [slug, refreshKey],
  );
  const orders = useApiGet<PurchaseOrderListResponse>(
    slug ? `${base}/api/v1/procurement/orders` : null,
    [slug, refreshKey],
  );

  function resetSupplierForm() {
    setName('');
    setCode('');
    setKind('organization');
    setMobile('');
    setPhone('');
    setNationalId('');
    setEconomicCode('');
    setAddress('');
    setFormError(null);
    setFieldErrors({});
  }

  async function onSaveSupplier(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setFieldErrors({ name: 'نام تأمین‌کننده الزامی است.' });
      setFormError('نام تأمین‌کننده الزامی است.');
      return;
    }
    const finalCode = code.trim() || `SUP-${Math.floor(100000 + Math.random() * 900000)}`;
    setIsSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    try {
      // B36: map individual to person for database check constraint
      await apiPost(`${base}/api/v1/master-data/parties`, {
        name: name.trim(),
        code: finalCode,
        kind: kind === 'individual' ? 'person' : 'organization',
        mobile: mobile.trim() || undefined,
        phone: phone.trim() || undefined,
        nationalId: nationalId.trim() || undefined,
        economicCode: economicCode.trim() || undefined,
        address: address.trim() || undefined,
        roles: ['supplier'],
      });
      resetSupplierForm();
      setModalOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError && err.envelope?.error?.details?.length) {
        const errors: Record<string, string> = {};
        for (const d of err.envelope.error.details) {
          if (d.path) errors[d.path] = d.message;
        }
        setFieldErrors(errors);
        setFormError(err.envelope.error.message || 'اطلاعات واردشده معتبر نیست.');
      } else {
        setFormError(
          err instanceof ApiError ? err.envelope.error.message : 'ثبت تأمین‌کننده با خطا مواجه شد.',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const suppliers = useMemo<SupplierRow[]>(() => {
    if (parties.status !== 'ready') return [];
    const statsByRef = new Map<
      string,
      { openOrders: number; totalOrders: number; lastOrderDate: string }
    >();
    if (orders.status === 'ready') {
      for (const o of orders.data.orders) {
        const existing = statsByRef.get(o.supplierPartyRef);
        const isOpen = OPEN_ORDER_STATUSES.has(orderStatusOf(o.statusLabel) ?? '');
        statsByRef.set(o.supplierPartyRef, {
          openOrders: (existing?.openOrders ?? 0) + (isOpen ? 1 : 0),
          totalOrders: (existing?.totalOrders ?? 0) + 1,
          lastOrderDate:
            existing === undefined || o.orderDate > existing.lastOrderDate
              ? o.orderDate
              : existing.lastOrderDate,
        });
      }
    }
    return parties.data.items
      .map((p) => {
        const ref = String(p['code']);
        const stats = statsByRef.get(ref);
        return {
          id: ref,
          supplierPartyRef: ref,
          displayName: String(p['name'] ?? ref),
          openOrders: stats?.openOrders ?? 0,
          totalOrders: stats?.totalOrders ?? 0,
          lastOrderDate: stats?.lastOrderDate ?? '',
        };
      })
      .sort((a, b) => b.lastOrderDate.localeCompare(a.lastOrderDate));
  }, [parties, orders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter(
      (s) =>
        s.supplierPartyRef.toLowerCase().includes(q) || s.displayName.toLowerCase().includes(q),
    );
  }, [suppliers, query]);

  if (!slug) return null;

  const columns: readonly V2TableColumn<SupplierRow>[] = [
    {
      key: 'supplier',
      header: 'تأمین‌کننده',
      render: (row) => (
        <span>
          {row.displayName} <span className="ltr sk-fg-muted">({row.supplierPartyRef})</span>
        </span>
      ),
    },
    {
      key: 'open',
      header: 'سفارش‌های باز',
      align: 'end',
      render: (row) => <span className="sk-num">{row.openOrders.toLocaleString('fa-IR')}</span>,
    },
    {
      key: 'total',
      header: 'کل سفارش‌ها',
      align: 'end',
      render: (row) => <span className="sk-num">{row.totalOrders.toLocaleString('fa-IR')}</span>,
    },
    {
      key: 'last',
      header: 'آخرین سفارش',
      render: (row) => <span className="sk-num">{row.lastOrderDate}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <Link
          to={`${base}/procurement/orders?status=&supplier=${row.supplierPartyRef}`}
          className="sk-btn sk-btn--secondary sk-btn--sm"
        >
          مشاهده سفارش‌ها
        </Link>
      ),
    },
  ];

  const totalCount = parties.status === 'ready' ? parties.data.total : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-xl)', fontWeight: 700 }}>تأمین‌کنندگان</h1>
          <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
            فهرست طرف‌های حساب دارای نقش «تأمین‌کننده» و تاریخچه سفارش‌های مرتبط
          </div>
        </div>
        <V2Button
          variant="primary"
          icon="plus"
          onClick={() => {
            resetSupplierForm();
            setModalOpen(true);
          }}
        >
          + ثبت سریع تأمین‌کننده
        </V2Button>
      </div>

      {parties.status === 'loading' ? <LoadingState /> : null}
      {parties.status === 'error' ? (
        <StatePanel
          variant={parties.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={parties.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {parties.status === 'ready' ? (
        <V2DataGrid
          icon="building"
          title="تأمین‌کنندگان"
          subtitle={`${(query ? filtered.length : totalCount).toLocaleString('fa-IR')} تأمین‌کننده`}
          actions={
            <V2Button
              variant="primary"
              size="sm"
              icon="plus"
              onClick={() => {
                resetSupplierForm();
                setModalOpen(true);
              }}
            >
              + ثبت سریع تأمین‌کننده
            </V2Button>
          }
          search={{ value: query, onChange: setQuery, placeholder: 'نام تأمین‌کننده' }}
          columns={columns}
          rows={filtered}
          rowKey={(r) => r.id}
          zebra
          emptyState={
            <V2EmptyState
              icon="building"
              title="تأمین‌کننده‌ای برای نمایش وجود ندارد."
              description="طرف‌حسابی را در اطلاعات پایه با نقش «تأمین‌کننده» علامت بزنید یا تأمین‌کننده جدید ثبت کنید."
              action={
                <V2Button
                  variant="primary"
                  icon="plus"
                  onClick={() => {
                    resetSupplierForm();
                    setModalOpen(true);
                  }}
                >
                  + ثبت سریع تأمین‌کننده
                </V2Button>
              }
            />
          }
        />
      ) : null}

      <Modal
        open={modalOpen}
        title="ثبت سریع تأمین‌کننده جدید"
        onClose={() => {
          if (!isSubmitting) setModalOpen(false);
        }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
            <V2Button
              type="button"
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => setModalOpen(false)}
            >
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              onClick={onSaveSupplier}
            >
              {isSubmitting ? 'در حال ثبت…' : 'ثبت تأمین‌کننده'}
            </V2Button>
          </div>
        }
      >
        <form
          onSubmit={onSaveSupplier}
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          {formError ? (
            <div
              style={{
                padding: '8px 12px',
                background: 'var(--sk-danger-soft)',
                color: 'var(--sk-danger)',
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              {formError}
            </div>
          ) : null}
          <V2FormGrid>
            <V2FormField
              label="نام تأمین‌کننده / شرکت"
              required
              width="wide"
              error={fieldErrors['name']}
            >
              <input
                className="sk-input"
                placeholder="مثال: شرکت صنایع فولاد البرز"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (fieldErrors['name']) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next['name'];
                      return next;
                    });
                  }
                }}
                autoFocus
              />
            </V2FormField>
            <V2FormField label="کد طرف‌حساب" width="medium" error={fieldErrors['code']}>
              <input
                className="sk-input ltr"
                placeholder="خالی بگذارید تا خودکار تولید شود"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="نوع شخصیت" width="medium">
              <select
                className="sk-select"
                value={kind}
                onChange={(e) => setKind(e.target.value as 'organization' | 'individual')}
              >
                <option value="organization">حقوقی (شرکت / سازمان)</option>
                <option value="individual">حقیقی (شخص)</option>
              </select>
            </V2FormField>
            <V2FormField label="شماره همراه" width="medium" error={fieldErrors['mobile']}>
              <input
                className="sk-input ltr"
                placeholder="0912..."
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="تلفن ثابت" width="medium" error={fieldErrors['phone']}>
              <input
                className="sk-input ltr"
                placeholder="021..."
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </V2FormField>
            <V2FormField
              label={kind === 'organization' ? 'شناسه ملی' : 'کد ملی'}
              width="medium"
              error={fieldErrors['nationalId']}
            >
              <input
                className="sk-input ltr"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="کد اقتصادی" width="medium" error={fieldErrors['economicCode']}>
              <input
                className="sk-input ltr"
                value={economicCode}
                onChange={(e) => setEconomicCode(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="نشانی" width="full" error={fieldErrors['address']}>
              <textarea
                className="sk-textarea"
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </V2FormField>
          </V2FormGrid>
        </form>
      </Modal>
    </div>
  );
}
